import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
  Home, 
  GraduationCap, 
  Bot, 
  Calculator,
  HelpCircle,
  User,
  LogOut,
  Globe,
  TrendingUp,
  Menu,
  X,
  MessageCircle,
  Settings,
  Bell,
  Target,
  Scale,
  Brain,
  PieChart,
  Award,
  Shield,
  BarChart3,
  Users,
  Wallet,
  Building2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

export const Layout = ({ children, hideNav = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, toggleLanguage, language } = useLanguage();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const navItems = [
    { path: '/dashboard', icon: Home, label_en: 'Home', label_sw: 'Nyumbani' },
    { path: '/chat', icon: Bot, label_en: 'AI Coach', label_sw: 'Mshauri AI' },
    { path: '/intelligence', icon: Brain, label_en: 'Intelligence', label_sw: 'Akili' },
    { path: '/utt-amis', icon: PieChart, label_en: 'UTT AMIS', label_sw: 'UTT AMIS' },
    { path: '/faida', icon: Building2, label_en: 'Faida Fund', label_sw: 'Faida Fund' },
    { path: '/wallet', icon: Wallet, label_en: 'Wallet', label_sw: 'Pochi' },
    { path: '/goals', icon: Target, label_en: 'Goals', label_sw: 'Malengo' },
    { path: '/etfs', icon: BarChart3, label_en: 'ETFs', label_sw: 'ETFs' },
    { path: '/simulator', icon: Calculator, label_en: 'Simulator', label_sw: 'Kihesabu' },
    { path: '/learn', icon: GraduationCap, label_en: 'Learn', label_sw: 'Jifunze' },
    { path: '/find-educator', icon: Users, label_en: 'Educators', label_sw: 'Walimu' },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50 hidden lg:block">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div 
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate('/dashboard')}
            >
              <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-bold text-lg text-foreground">Wekeza-Smart</span>
            </div>

            {/* Desktop Nav */}
            <nav className="flex items-center gap-1">
              {navItems.map(item => (
                <Button
                  key={item.path}
                  variant={isActive(item.path) ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => navigate(item.path)}
                  className={`${isActive(item.path) ? 'bg-primary/10 text-primary' : ''}`}
                  data-testid={`nav-${item.path.replace('/', '')}`}
                >
                  <item.icon className="w-4 h-4 mr-2" />
                  {t(item.label_en, item.label_sw)}
                </Button>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={toggleLanguage}
                className="flex items-center gap-2"
                data-testid="header-language-toggle"
              >
                <Globe className="w-4 h-4" />
                {language === 'en' ? 'SW' : 'EN'}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <span className="hidden sm:inline">{user?.name?.split(' ')[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem disabled className="text-muted-foreground">
                    {user?.email || user?.phone}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/educators')} className="cursor-pointer">
                    <Award className="w-4 h-4 mr-2" />
                    {t('Educator Program', 'Programu ya Walimu')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/rebalance')} className="cursor-pointer">
                    <Scale className="w-4 h-4 mr-2" />
                    {t('Smart Rebalance', 'Urekebishaji')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/alerts')} className="cursor-pointer">
                    <Bell className="w-4 h-4 mr-2" />
                    {t('Price Alerts', 'Arifa za Bei')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/investor-profile')} className="cursor-pointer">
                    <User className="w-4 h-4 mr-2" />
                    {t('Investor Profile', 'Wasifu wa Mwekezaji')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />
                    {t('Settings', 'Mipangilio')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('Logout', 'Ondoka')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50 lg:hidden">
        <div className="flex items-center justify-between h-14 px-4">
          <div 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/dashboard')}
          >
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-bold text-foreground">Wekeza-Smart</span>
          </div>

          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={toggleLanguage}
              className="w-9 h-9"
            >
              <Globe className="w-4 h-4" />
            </Button>

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="w-9 h-9">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] p-0">
                <div className="flex flex-col h-full">
                  <div className="p-4 border-b border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{user?.name}</p>
                        <p className="text-xs text-muted-foreground">{user?.email || user?.phone}</p>
                      </div>
                    </div>
                  </div>
                  <nav className="flex-1 p-2">
                    {navItems.map(item => (
                      <button
                        key={item.path}
                        onClick={() => {
                          navigate(item.path);
                          setMobileMenuOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          isActive(item.path) 
                            ? 'bg-primary/10 text-primary' 
                            : 'text-foreground hover:bg-muted'
                        }`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span>{t(item.label_en, item.label_sw)}</span>
                      </button>
                    ))}
                  </nav>
                  <div className="p-4 border-t border-border">
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={handleLogout}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('Logout', 'Ondoka')}
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={`${hideNav ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'}`}>
        {children}
      </main>

      {/* Disclaimer Footer - Desktop only */}
      <footer className={`${hideNav ? 'hidden' : 'hidden lg:block'} bg-muted/30 border-t border-border/50 py-4 px-4 mt-auto`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <Shield className="w-3.5 h-3.5" />
                <span>
                  {t(
                    'Market data updated daily • For informational purposes only • Not financial advice',
                    'Data ya soko inasasishwa kila siku • Kwa madhumuni ya taarifa tu • Si ushauri wa kifedha'
                  )}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <a href="mailto:info@wekeza-smart.co.tz" className="hover:text-foreground transition-colors">
                  info@wekeza-smart.co.tz
                </a>
                <span>|</span>
                <a href="mailto:support@wekeza-smart.co.tz" className="hover:text-foreground transition-colors">
                  support@wekeza-smart.co.tz
                </a>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground border-t border-border/30 pt-3">
              <span>© 2026 Wekeza-Smart. {t('All rights reserved.', 'Haki zote zimehifadhiwa.')}</span>
              <span className="text-muted-foreground/60">
                {t('Regulated by CMSA & BOT', 'Inadhibitiwa na CMSA & BOT')}
              </span>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button - Hidden on chat pages to avoid overlap with input */}
      {!location.pathname.includes('/whatsapp') && !location.pathname.includes('/chat') && (
        <button
          onClick={() => navigate('/whatsapp')}
          className="fixed bottom-24 lg:bottom-6 right-4 z-40 w-14 h-14 rounded-full bg-[#25D366] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center"
          data-testid="whatsapp-fab"
          aria-label="Open WhatsApp Chat"
        >
          <MessageCircle className="w-7 h-7" />
        </button>
      )}

      {/* Mobile Bottom Navigation */}
      {!hideNav && (
        <nav className="fixed bottom-0 left-0 right-0 z-50 glass border-t border-border/50 lg:hidden pb-safe">
          <div className="flex items-center justify-around h-16">
            {navItems.slice(0, 5).map(item => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`mobile-nav-item flex flex-col items-center justify-center w-16 h-full ${
                  isActive(item.path) ? 'text-primary' : 'text-muted-foreground'
                }`}
                data-testid={`mobile-nav-${item.path.replace('/', '')}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-[10px] mt-1">
                  {t(item.label_en, item.label_sw)}
                </span>
              </button>
            ))}
          </div>
        </nav>
      )}

      {/* Spacer for mobile bottom nav */}
      {!hideNav && <div className="h-16 lg:hidden" />}
    </div>
  );
};

export default Layout;
